import React, { useCallback, useEffect, useRef } from 'react';
import './tracking.scss';
import { useApiTracking } from '../../api/api.tracking';
import { Column, Paging, Scrolling, Selection } from 'devextreme-react/data-grid';
import { InfoPanelOrder } from '../../components/utils/info-panels/InfoPanelOrder';
import { InfoPanelPatient } from '../../components/utils/info-panels/InfoPanelPatient';
import { DataGrid, Tooltip, TextBox, SelectBox, Toolbar, LoadPanel, Button, ScrollView, LoadIndicator } from 'devextreme-react';
import { Item } from 'devextreme-react/toolbar';
import { useAppDispatch, useAppSelector } from '../../contexts/hooks';
import { shallowEqual } from 'react-redux';
import { RightPanel } from '../../components/utils/right-panel/RighPanel';
import moment from 'moment';
import { FilterComponent } from '../find-results/Modals/FilterOrders';
import { useApiSearch } from '../../api/api.Search';
import { ConvertDataPatient, ConvertDataOrder, ConvertLocalDate } from '../../utils/functions';
import { TrackingProgressBar } from '../../components/utils/tracking-progress-bar/TrackingProgressBar';
import { newToast } from '../../components';
import { useScreenSize } from '../../utils/media-query';
import { CreateOrders, Pin, resGridStatus3, UnPin } from '../../images/icons';
import { setPanelPined, setPanelVisible } from '../../contexts/slices/orders';
import { useApiOrders } from '../../api/api.orders';
import ConfirmationPopup from '../../components/utils/popup-confirmation/PopupConfirmation';

const Index = () => {
  const { getTracking, getAllStates, getSampleTypes, setTracking, getBarcodes } = useApiTracking();
  const ColumnsContainerOrderOrders = useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ColumnsContainerOrderOrders')[0], shallowEqual)?.Cgn_Resultado;
  const [currentPatient, setCurrentPatient] = React.useState<any>(null);
  const Prm_Ord = useAppSelector((x) => x.userInfo.Permissions?.Prm_Ord, shallowEqual) ?? false;
  const ColumnsContainerPatientOrders = useAppSelector((x) => x.configuration.Data?.Config_General.filter((x) => x.Cgn_Nombre === 'ColumnsContainerPatientOrders')[0], shallowEqual)?.Cgn_Resultado;
  const [currentOrder, setCurrentOrder] = React.useState<any>(null);
  const [data, setData] = React.useState<any[]>([]);
  const [trackingData, setTrackingData] = React.useState<any[]>([]);
  const [allStates, setAllStates] = React.useState<any[]>([]);
  const [selectedSampleType, setSelectedSampleType] = React.useState<number | null>(null);
  const [sampleTypes, setSampleTypes] = React.useState<any[]>([]);
  const [selectedBarcode, setSelectedBarcode] = React.useState<number | null>(null);
  const [barcodes, setBarcodes] = React.useState<any[]>([]);
  const [highlightedSampleId, setHighlightedSampleId] = React.useState<number | null>(null);
  const [isTracking, setIsTracking] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const { SearchOrder } = useApiSearch();
  const gridRef = useRef<DataGrid>(null);
  const barcodeRef = useRef<TextBox>(null);
  const [modalVisible, setModalVisible] = React.useState(false);
  const [confirmModalVisible, setConfirmModalVisible] = React.useState(false);
  const { isMedium, isLarge, isSmall, isXSmall } = useScreenSize();
  const panelVisible = useAppSelector((x) => x.globalOrders.PanelVisible, shallowEqual);
  const dispatch = useAppDispatch();
  const { findOrder, getHeaderOrderById } = useApiOrders();
  useEffect(() => {
    // Cargar todos los estados disponibles al iniciar
    getAllStates().then((res) => {
      if (res && Array.isArray(res)) {
        setAllStates(res.sort((a, b) => a.Tke_Orden - b.Tke_Orden));
      }
    });

    // Cargar todos los tipos de muestra disponibles
    getSampleTypes().then((res) => {
      if (res && Array.isArray(res)) {
        setSampleTypes(res.filter((t) => t.Tm_Estado === true));
      }
    });

    // Cargar todos los códigos de barras disponibles
    getBarcodes().then((res) => {
      if (res && Array.isArray(res)) {
        setBarcodes(res);
      }
    });

    // Cargar datos del día actual por defecto
    const filters = [
      {
        Field: 'Ord_FechaCreacion',
        TypeFilter: 'Between',
        Value: ConvertLocalDate(new Date()),
        Value2: ConvertLocalDate(new Date()),
      },
    ];
    handleApplyFilters(filters);
  }, []);

  const handleClickFilter = () => {
    setModalVisible(true);
  };

  const handleClickRefresh = () => {
    if (currentOrder) {
      fetchData(currentOrder.Ord_Id, undefined, false); // autoAdvance = false para refresh manual
    }
  };

  const handleClickViewPatient = () => {
    // Lógica para ver paciente
  };

  const fetchData = useCallback(
    async (idOrd: number, OrdNumber?: string, autoAdvance: boolean = false) => {
      // Obtener información de la orden y paciente
      getHeaderOrderById(idOrd).then((res) => {
        if (res) {
          const { Orden, Paciente } = res;
          const convertedData = ConvertDataPatient(Paciente);
          const convertedOrder = ConvertDataOrder(Orden);
          setCurrentPatient(convertedData);
          setCurrentOrder(convertedOrder);
        } else {
          setCurrentOrder(null);
          setCurrentPatient(null);
        }
      });

      // Obtener datos de tracking para esta orden (siempre todos los tipos de muestra)
      getTracking(idOrd, undefined, undefined, OrdNumber, undefined).then((res) => {
        if (res && Array.isArray(res)) {
          setTrackingData(res);

          // Avanzar automáticamente si:
          // - La búsqueda es por código de barras (autoAdvance = true)
          // - Y hay solo 1 resultado O hay un tipo de muestra seleccionado O hay un código de barras seleccionado
          if (autoAdvance && (res.length === 1 || selectedSampleType !== null || selectedBarcode !== null)) {
            // Filtrar por tipo de muestra y/o código de barras si están seleccionados
            let filteredResults = res;
            if (selectedSampleType) {
              filteredResults = filteredResults.filter((r) => r.Tk_Tm_Id === selectedSampleType);
            }
            if (selectedBarcode) {
              filteredResults = filteredResults.filter((r) => r.CodBarras && r.CodBarras.Cb_Id === selectedBarcode);
            }

            if (filteredResults.length === 1) {
              const tracking = filteredResults[0];

              // Resaltar visualmente la muestra
              setHighlightedSampleId(tracking.Tk_Tm_Id);

              // Quitar el resaltado después de 800ms (más rápido)
              setTimeout(() => {
                setHighlightedSampleId(null);
              }, 800);

              // Avanzar esta muestra al siguiente estado
              handleAdvanceSample(tracking.Tk_Ord_Id, tracking.Tk_Id, tracking.Tk_Tke_Id);
            }
          }
        } else {
          setTrackingData([]);
        }
      });
    },
    [getHeaderOrderById, getTracking, selectedSampleType, selectedBarcode]
  );

  const handleFocusedRowChanged = (e) => {
    const selectedRow = e.row.data; // Aquí accedemos a la fila seleccionada

    if (selectedRow) {
      // Si la fila es válida, obtenemos su ID y ejecutamos `findOrder`
      const rowId = selectedRow.Ord_Id;
      fetchData(rowId, undefined, false); // autoAdvance = false para búsqueda desde grid
    }
  };

  const rowPrepared = (e) => {
    if (e.rowType === 'data') {
      const color = e.data.Se_Color;
      if (color && color.trim() !== '') {
        e.rowElement.style.color = color;
      }
      e.rowElement.setAttribute('id', `row-${e.data.Ord_Id}`);
    }
  };

  const handleApplyFilters = (filters) => {
    setModalVisible(false);
    SearchOrder(filters).then((res) => {
      if (res.length > 0) {
        setData(res);
      } else {
        setData([]);
      }
    });
  };

  const handleCloseModal = () => {
    setModalVisible(false);
  };

  const handleAdvanceSample = async (orderId: number, idTk: number, tkeId: number) => {
    // Mostrar indicador de carga
    setIsTracking(true);

    // Creamos un array con la información para el tracking [{IdTk, IdOrd, IdTkEst}]
    const trackingUpdate = [{ IdTk: idTk, IdOrd: orderId, IdTkEst: tkeId }];

    try {
      const response = await setTracking(trackingUpdate);
      // Verificar si la respuesta es válida
      if (response.length > 0) {
        setTrackingData(response);
        newToast('SUCCESS', 'Tracking actualizado correctamente', 3);
        // Resaltar visualmente la muestra que se acaba de actualizar
        setHighlightedSampleId(idTk);
        setTimeout(() => {
          setHighlightedSampleId(null);
        }, 800);
      }
    } finally {
      // Ocultar indicador de carga
      setIsTracking(false);
    }
  };

  const handleRejectSample = (orderId: number, tmId: number) => {
    // La lógica de rechazo se maneja dentro de TrackingProgressBar
  };

  const handleValidateAllSamples = () => {
    if (!currentOrder || !trackingData || trackingData.length === 0 || allStates.length === 0) {
      newToast('WARNING', 'Seleccione al menos una orden con muestras', 3);
      return;
    }

    // Obtener el último estado (el de mayor orden)
    const lastState = allStates[allStates.length - 1];

    // Filtrar las muestras que NO están en el último estado Y que NO estén descartadas
    const samplesToAdvance = trackingData.filter((tracking) => tracking.Tk_Tke_Id !== null && tracking.Tk_Tke_Id !== lastState.Tke_Id);

    if (samplesToAdvance.length === 0) {
      newToast('INFO', 'Todas las muestras han finalizado el proceso de tracking o están descartadas', 3);
      return;
    }

    // Mostrar modal de confirmación
    setConfirmModalVisible(true);
  };

  const handleConfirmValidateAll = async () => {
    setConfirmModalVisible(false);

    if (!currentOrder || !trackingData || trackingData.length === 0 || allStates.length === 0) {
      return;
    }

    // Obtener el último estado (el de mayor orden)
    const lastState = allStates[allStates.length - 1];

    // Filtrar las muestras que NO están en el último estado Y que NO estén descartadas
    const samplesToAdvance = trackingData.filter((tracking) => tracking.Tk_Tke_Id !== null && tracking.Tk_Tke_Id !== lastState.Tke_Id);

    if (samplesToAdvance.length === 0) {
      return;
    }

    // Mostrar indicador de carga
    setIsTracking(true);

    try {
      // Crear el array de tracking updates para todas las muestras que deben avanzar
      const trackingUpdates = samplesToAdvance.map((tracking) => ({
        IdTk: tracking.Tk_Id,
        IdOrd: tracking.Tk_Ord_Id,
        IdTkEst: tracking.Tk_Tke_Id, // Estado actual, el API avanzará al siguiente
      }));

      // Enviar todas las actualizaciones
      const response = await setTracking(trackingUpdates);

      // Verificar si la respuesta es válida
      if (response && response.length > 0) {
        setTrackingData(response);
        newToast('SUCCESS', `${samplesToAdvance.length} muestra(s) avanzada(s) correctamente`, 3);
      }
    } catch (error) {
      console.error('Error al validar muestras:', error);
      newToast('ERROR', 'Error al validar las muestras', 3);
    } finally {
      // Ocultar indicador de carga
      setIsTracking(false);
    }
  };

  const handleCancelValidateAll = () => {
    setConfirmModalVisible(false);
  };

  const handleBarcodeChange = (e: any) => {
    if (!e.value) {
      return;
    }

    // Siempre buscar por código de barras sin importar si hay orden seleccionada
    getTracking(undefined, undefined, undefined, e.value, undefined).then((res) => {
      if (res && Array.isArray(res) && res.length > 0) {
        // Obtener el ID de la orden del primer resultado
        const orderId = res[0].Tk_Ord_Id;
        fetchData(orderId, e.value, true); // autoAdvance = true para búsqueda por código
      } else {
        // No se encontró ningún tracking con ese código de barras
        setTrackingData([]);
        setCurrentOrder(null);
        setCurrentPatient(null);
      }
    });

    // Limpiar el campo de texto
    setTimeout(() => {
      barcodeRef.current?.instance.reset();
      barcodeRef.current?.instance.focus();
    }, 100);
  };

  const handlePanelClose = () => {
    dispatch(setPanelVisible(true));
    dispatch(setPanelPined(false));
  };

  const handleClickViewOrder = () => {
    if (currentOrder) {
      findOrder(undefined, currentOrder.Ord_Id, setLoading, true);
    }
  };

  return (
    <div className='view crm-contact-list'>
      <div className='view-wrapper-contact-list'>
        <div style={{ width: '100%', height: 'calc(100vh - 100px)' }}>
          <div className='view-wrapper view-wrapper-contact-details'>
            <div className='container-forms-ord'>
              <div className={`colOrd-${ColumnsContainerOrderOrders ? ColumnsContainerOrderOrders : 6}`}>
                <InfoPanelOrder OrdertData={currentOrder} ModuleName='Buscar ordenes' handleClickFilter={handleClickFilter} handleClickRefresh={handleClickRefresh} />
              </div>
              <div className={`colOrd-${ColumnsContainerPatientOrders ? ColumnsContainerPatientOrders : 6}`}>
                <InfoPanelPatient PatientData={currentPatient} handleClickViewPatient={handleClickViewPatient} />
              </div>
            </div>

            {/* Toolbar para código de barras */}
            <div className='tracking-toolbar-container'>
              <Toolbar>
                <Item location='before' visible={isLarge || isMedium}>
                  <div className='toolbar-label'>Código deBarras:</div>
                </Item>
                <Item location='before' visible={isLarge || isMedium}>
                  <TextBox ref={barcodeRef} onValueChanged={handleBarcodeChange} placeholder='Escanee o digite el código de barras' width={300} stylingMode='outlined' />
                </Item>
                <Item location='before' visible={isLarge || isMedium}>
                  <div className='toolbar-label'>Código de Barras:</div>
                </Item>
                <Item location='before' visible={isLarge || isMedium}>
                  <SelectBox dataSource={barcodes} displayExpr='Cb_Nombre' valueExpr='Cb_Id' value={selectedBarcode} onValueChanged={(e) => setSelectedBarcode(e.value)} placeholder='Todos los códigos' width={200} stylingMode='outlined' showClearButton={true} searchEnabled={true} searchMode='contains' searchExpr='Cb_Nombre' />
                </Item>
                <Item location='before' visible={isLarge || isMedium}>
                  <div className='toolbar-label'>Tipo de Muestra:</div>
                </Item>
                <Item location='before' visible={isLarge || isMedium}>
                  <SelectBox dataSource={sampleTypes} displayExpr='Tm_Nombre' valueExpr='Tm_Id' value={selectedSampleType} onValueChanged={(e) => setSelectedSampleType(e.value)} placeholder='Todas las muestras' width={200} stylingMode='outlined' showClearButton={true} searchEnabled={true} searchMode='contains' searchExpr='Tm_Nombre' />
                </Item>

                <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
                  <Button id='ValidarResult' icon={resGridStatus3} text='Validar resultado' onClick={handleValidateAllSamples} stylingMode='text' />
                  <Tooltip target={'#ValidarResult'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Confirmar todas las muestras.
                  </Tooltip>
                </Item>
                <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton' visible={currentOrder !== null && trackingData.length > 0 && Prm_Ord}>
                  <Button id='ViewOrder' icon={CreateOrders} text='Ver orden' onClick={handleClickViewOrder} stylingMode='text' />
                  <Tooltip target={'#ViewOrder'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    Ver orden
                  </Tooltip>
                </Item>

                <Item location='after' locateInMenu='never' showText='inMenu' widget='dxButton'>
                  <Button id='showpanel' icon={panelVisible ? Pin : UnPin} text={panelVisible ? 'Panel Visible' : 'Panel oculto'} stylingMode='text' onClick={handlePanelClose} />
                  <Tooltip target={'#showpanel'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
                    {panelVisible ? 'Ocultar panel' : 'Abrir panel'}
                  </Tooltip>
                </Item>
              </Toolbar>
            </div>
            <div className='detail-values-container'>
              <ScrollView height='calc(100vh - 300px)' style={{ paddingBottom: isSmall || isXSmall ? `150px` : `50px` }}>
                <TrackingProgressBar trackingData={trackingData} allStates={allStates} currentOrder={currentOrder} onAdvance={handleAdvanceSample} onReject={handleRejectSample} onTrackingUpdate={setTrackingData} highlightedSampleId={highlightedSampleId} />
              </ScrollView>
            </div>
          </div>
        </div>

        <RightPanel>
          <DataGrid dataSource={data} tabIndex={0} height='100%' onFocusedRowChanged={handleFocusedRowChanged} ref={gridRef} focusedRowEnabled={true} keyExpr='Ord_Id' id='currentOrders' onRowPrepared={rowPrepared} className='gridOrders compact-grid' noDataText='No hay órdenes para mostrar' hoverStateEnabled={true} repaintChangesOnly={true}>
            <Paging enabled={false} />
            <Selection mode='single' />
            <Scrolling mode='virtual' rowRenderingMode='virtual' />
            <Column dataField='Ord_Numero' caption='ORDEN' width={100} />
            <Column caption='USUARIO' calculateCellValue={(rowData) => `${rowData.Pc_Nombre || ''} ${rowData.Pc_Apellido1 || ''} ${rowData.Pc_Apellido2 || ''}`} />
          </DataGrid>
        </RightPanel>
      </div>
      {data.map((order) => (
        <Tooltip key={order.Ord_Id} target={`#row-${order.Ord_Id}`} showEvent='mouseenter' hideEvent='mouseleave' position='left'>
          <div className='toolTipFilter'>
            <div>
              <strong>Fecha de creación:</strong> {moment(order.Ord_FechaCreacion).format('DD/MM/YYYY')}
            </div>
            <div>
              <strong>Orden:</strong> {order.Ord_Numero}
            </div>
            <div>
              <strong>Usuario:</strong> {order.Pc_Nombre || ''} {order.Pc_Apellido1 || ''} {order.Pc_Apellido2 || ''}
            </div>
          </div>
        </Tooltip>
      ))}
      <FilterComponent onApplyFilters={handleApplyFilters} visible={modalVisible} setVisible={handleCloseModal} includeOrderFields={true} includePatientFields={true} />

      {/* Modal de confirmación para validar todas las muestras */}
      {confirmModalVisible && <ConfirmationPopup visible={confirmModalVisible} onConfirm={handleConfirmValidateAll} onCancel={handleCancelValidateAll} message={`Se avanzarán ${trackingData.filter((tracking) => tracking.Tk_Tke_Id !== null && tracking.Tk_Tke_Id !== allStates[allStates.length - 1]?.Tke_Id).length} muestra(s) al siguiente estado`} loading={isTracking} />}

      {/* Indicador de carga mientras se hace tracking */}
      <LoadPanel visible={isTracking} message='Actualizando tracking...' showPane={true} shading={true} shadingColor='rgba(0, 0, 0, 0.4)' />
    </div>
  );
};

export default Index;
